// This file replicates functionality from ./deprecated-columns in the same directory but modifies all columns to support DataGrid instead of BasicTable

import React from 'react';
import styled, { css } from 'styled-components';
import type { ColDef, IRowNode } from 'ag-grid-community';
import { compact, isNil } from 'lodash';

import type { Theme } from 'venn-ui-kit';
import { BodyHighlight, CellLoader, GetColor, Icon, Tooltip, TooltipPosition } from 'venn-ui-kit';
import { assertNotNil, capitalizeFirstLetter, getFormattedFrequency, itemHasNoReturns, Numbers } from 'venn-utils';
import type {
  AnalysisRequest,
  ComputedInvestmentResidual,
  RangeAnalysisResponse,
  SubjectRangeAnalysis,
} from 'venn-api';
import type { InvestmentOverrideType } from 'venn-state';

import type { BulkManageRow, BulkManageUpdateFn } from './types';
import { BulkManageAction } from './types';
import InvestmentActions from '../investment-actions/InvestmentActions';
import { formatDate } from '../manage-data/utils';
import ForecastModalLauncherSpanButton from '../modals/forecasts/ForecastModalLauncherSpanButton';
import AnalysisPeriodLegend from './AnalysisPeriodLegend';
import { atStartOfDay, getDataColor, getRangeLogic, getRowRangeLogic } from './analysisPeriodUtils';
import ProxiedInvestmentTooltipContent from './ProxiedInvestmentTooltipContent';

const DATE_COLUMN_MIN_WIDTH = 95;

const getDataSource = (): ColDef => {
  return {
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { isLive, dataSource, isIntegration } = node.data;

      return (
        <DefaultCell disabled={isLive === false}>
          {dataSource} {isIntegration && <Icon type="exchange" prefix="fas" />}
        </DefaultCell>
      );
    },
    headerName: 'Data Source',
    flex: 1,
    comparator: bulkManageSortFunction((r) => r.dataSource),
  };
};

const getDateCreated = (): ColDef => {
  return {
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { created } = node.data;

      return <DefaultCell>{formatDate(created)}</DefaultCell>;
    },
    headerName: 'Date Created',
    flex: 1,
    minWidth: DATE_COLUMN_MIN_WIDTH,
    comparator: bulkManageSortFunction((r) => r.created),
  };
};

const getDateUpdated = (): ColDef => {
  return {
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { updated } = node.data;

      return <DefaultCell>{formatDate(updated)}</DefaultCell>;
    },
    headerName: 'Date Updated',
    flex: 1,
    minWidth: DATE_COLUMN_MIN_WIDTH,
    comparator: bulkManageSortFunction((r) => r.updated),
  };
};

const getCreatedBy = (): ColDef => {
  return {
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { owner } = node.data;

      return <DefaultCell>{owner?.displayName}</DefaultCell>;
    },
    headerName: 'Created By',
    flex: 1,
    sortable: false,
  };
};

const getUpdatedBy = (): ColDef => {
  return {
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { updatedBy } = node.data;

      return <DefaultCell>{updatedBy?.displayName}</DefaultCell>;
    },
    headerName: 'Updated By',
    flex: 1,
    sortable: false,
  };
};

const getFrequencyColumn = (): ColDef => {
  return {
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { rangeLoading, startDate, endDate, frequency } = node.data;

      if (rangeLoading) {
        return (
          <DefaultCell>
            <CellLoader />
          </DefaultCell>
        );
      }
      if (itemHasNoReturns(startDate, endDate)) {
        return getNoReturnsTooltippedCell(node.data, 'N/A');
      }

      return <DefaultCell>{capitalizeFirstLetter(getFormattedFrequency(frequency))}</DefaultCell>;
    },
    headerName: 'Frequency',
    flex: 1,
    comparator: bulkManageSortFunction((r) => r.frequency),
  };
};

const getForecastColumn = (
  canEditForecasts: boolean,
  onUpdate: BulkManageUpdateFn,
  getData: (c: ComputedInvestmentResidual) => {
    computedDataField: number | undefined;
    defaultDataField: number | undefined;
    isDataOverridden: boolean;
  },
  headerName: string,
  overrideType: InvestmentOverrideType,
  comparator: (
    valueA,
    valueB,
    nodeA: IRowNode<BulkManageRow>,
    nodeB: IRowNode<BulkManageRow>,
    isDescending: boolean,
  ) => number,
): ColDef => {
  return {
    headerName,
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { investmentForecastLoading, investmentForecast } = node.data;
      if (investmentForecastLoading) {
        return (
          <DefaultCell>
            <CellLoader />
          </DefaultCell>
        );
      }
      if (!investmentForecast) {
        return <ForecastInvestmentCell />;
      }
      const { computedDataField, defaultDataField, isDataOverridden } = getData(investmentForecast);
      const computedData = isDataOverridden ? (
        <StyledBodyHighlight>{Numbers.safeFormatPercentage(computedDataField, 1)}</StyledBodyHighlight>
      ) : (
        Numbers.safeFormatPercentage(computedDataField, 1)
      );

      return (
        <ForecastInvestmentCell>
          <>
            {!isNil(computedDataField) ? (
              computedData
            ) : (
              <StyledResidualSpan useRightMargin={!defaultDataField}>
                {Numbers.safeFormatPercentage(defaultDataField, 1)}
              </StyledResidualSpan>
            )}
            <ForecastModalLauncherSpanButton
              ctaPurpose="Edit forecasts from Manage Data page row"
              onResidualForecastUpdated={(targetId) =>
                onUpdate(BulkManageAction.INVESTMENT_FORECAST_MODIFIED, node.data, targetId)
              }
              onOpenResidualForecastTarget={investmentForecast}
              isReadOnly={!canEditForecasts}
              overrideType={overrideType}
              useTooltipPortal
            />
          </>
        </ForecastInvestmentCell>
      );
    },
    flex: 1,
    comparator,
  };
};

const getResidualForecastColumn = (canEditForecasts: boolean, onUpdate: BulkManageUpdateFn): ColDef => {
  return getForecastColumn(
    canEditForecasts,
    onUpdate,
    (c) => ({
      computedDataField: c.computedResidual,
      defaultDataField: c.defaultResidual,
      isDataOverridden: !isNil(c.overriddenResidual),
    }),
    'Forecast Residual',
    'residual',
    bulkManageSortFunction(
      (r) => r.investmentForecast?.computedResidual ?? r.investmentForecast?.defaultResidual,
      (r) => isNil(r.investmentForecast?.overriddenResidual) && isNil(r.investmentForecast?.defaultResidual),
    ),
  );
};

const getReturnForecastColumn = (canEditForecasts: boolean, onUpdate: BulkManageUpdateFn): ColDef => {
  return getForecastColumn(
    canEditForecasts,
    onUpdate,
    (c) => ({
      computedDataField: c.computedReturn,
      defaultDataField: c.defaultReturn,
      isDataOverridden: !isNil(c.overriddenReturn),
    }),
    'Forecast Return',
    'return',
    bulkManageSortFunction(
      (r) => r.investmentForecast?.computedReturn ?? r.investmentForecast?.defaultReturn,
      (r) => isNil(r.investmentForecast?.overriddenReturn) && isNil(r.investmentForecast?.defaultReturn),
    ),
  );
};

const getProxyInterpolationColumn = (canEditProxies: boolean, onUpdate: BulkManageUpdateFn): ColDef => {
  return {
    cellRenderer: ({ node, api }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { data, rowIndex } = node;

      if (!data.investment) {
        return null;
      }
      const count = api.getDisplayedRowCount();
      const dropdownDirection = count > 10 && rowIndex > count / 2 ? 'up' : 'down';
      return (
        <DefaultCell>
          <InvestmentActions
            fund={data.investment}
            context="bulk-management"
            onFundDataUpdated={() => onUpdate(BulkManageAction.FUND_MODIFIED, data, data.investmentId)}
            dropdownVerticalDirection={dropdownDirection}
            isReadOnly={!canEditProxies}
            usePortal
          />
        </DefaultCell>
      );
    },
    headerName: 'Proxy',
    sortable: false,
    flex: 2,
  };
};

const getStartColumn = (): ColDef => {
  return {
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { startDate } = node.data;

      return <DefaultCell>{startDate ? formatDate(startDate) : ''}</DefaultCell>;
    },
    headerName: 'Start Date',
    flex: 1,
    minWidth: DATE_COLUMN_MIN_WIDTH,
    comparator: bulkManageSortFunction((r) => r.startDate),
  };
};

const getEndColumn = (): ColDef => {
  return {
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      const { endDate } = node.data;

      return <DefaultCell>{endDate ? formatDate(endDate) : ''}</DefaultCell>;
    },
    headerName: 'End Date',
    flex: 1,
    minWidth: DATE_COLUMN_MIN_WIDTH,
    comparator: bulkManageSortFunction((r) => r.endDate),
  };
};

const getRangeColumn = (
  theme: Theme,
  hasFullHistory: boolean,
  rangeAnalysis?: RangeAnalysisResponse,
  primaryRangeAnalysis?: SubjectRangeAnalysis | null,
): ColDef => {
  if (!rangeAnalysis) {
    return {
      headerName: 'Analysis Period',
      cellRenderer: () => null,
      flex: 1,
    };
  }
  const { earliestStart, latestEnd, fullRange, overlap } = getRangeLogic(
    hasFullHistory,
    rangeAnalysis,
    primaryRangeAnalysis,
  );
  return {
    headerName: 'Analysis Period',
    headerComponentParams: AnalysisPeriodLegend,
    flex: 3,
    cellRenderer: ({ node }) => {
      if (node?.group || !node?.data) {
        return null;
      }

      if (node.data.rangeLoading) {
        return (
          <DefaultCell>
            <CellLoader />
          </DefaultCell>
        );
      }
      if (itemHasNoReturns(node.data.startDate, node.data.endDate)) {
        return getNoReturnsTooltippedCell(node.data, <TooltipTrigger />);
      }
      const frequency = assertNotNil(node.data.frequency);
      const startDate = assertNotNil(atStartOfDay(node.data.startDate));
      const endDate = assertNotNil(node.data.endDate);
      const zeroAllocation = node.data.allocation === 0;

      const { investmentColor, proxyColor } = getDataColor({
        theme,
        isGreyedOut: zeroAllocation,
        secondaryData: node.data.secondary ?? false,
      });

      const { proxy, investment, extrapolation } = getRowRangeLogic({
        startDate,
        endDate,
        fullRange,
        earliestStart,
        latestEnd,
        proxyStartDate: atStartOfDay(node.data.proxyStartDate),
        proxyEndDate: node.data.proxyEndDate,
        extrapolateEndDate: node.data.extrapolateEndDate,
        extrapolateStartDate: atStartOfDay(node.data.extrapolateStartDate),
      });
      const dashedRangeBar = frequency === 'QUARTERLY' || frequency === 'YEARLY';
      return (
        <RangeCell>
          {overlap.percentageWidth > 0 ? (
            <OverlapBar widthPercentage={overlap.percentageWidth} leftMarginPercentage={overlap.percentageStart} />
          ) : null}
          <RangeBar
            widthPercentage={investment.percentageWidth}
            leftMarginPercentage={investment.percentageStart}
            dashed={dashedRangeBar}
            color={investmentColor}
          >
            <StyledTooltip
              position={TooltipPosition.Top}
              maxWidth={400}
              content={<ProxiedInvestmentTooltipContent data={node.data} />}
              usePortal
            >
              {proxy.percentageWidth > 0 && (
                <RangeBar
                  widthPercentage={proxy.percentageWidth}
                  leftMarginPercentage={proxy.percentageStart}
                  color={proxyColor}
                  dashed={dashedRangeBar}
                />
              )}
              {extrapolation.percentageWidth > 0 && (
                <RangeBar
                  widthPercentage={extrapolation.percentageWidth}
                  leftMarginPercentage={extrapolation.percentageStart}
                  color={theme.Colors.DEPRECATED_DataBarColor.LightPaleBlue}
                  dashed={false}
                />
              )}
            </StyledTooltip>
          </RangeBar>
        </RangeCell>
      );
    },
    comparator: bulkManageSortFunction((r) => (r.endDate ?? 0) - (r.startDate ?? 0)), // Sort by range length
  };
};

export const getBulkManagementColumns = (
  rangeAnalysis: RangeAnalysisResponse | undefined,
  onRowUpdate: BulkManageUpdateFn,
  theme: Theme,
  canEditForecasts: boolean,
  canEditProxies: boolean,
  hasFullHistory: boolean,
  tableContainerWidth?: number,
  rangeAnalysisRequest?: Partial<AnalysisRequest>,
): ColDef[] => {
  const largeScreen: boolean = !isNil(tableContainerWidth) && tableContainerWidth > 1200;
  const primaryIndex = rangeAnalysisRequest?.subjects?.findIndex((s) => s.comparisonType === 'PRIMARY');
  const primaryRangeAnalysis = !isNil(primaryIndex) ? rangeAnalysis?.rangeAnalyses[primaryIndex] : null;

  return compact([
    getDataSource(),
    largeScreen && getDateCreated(),
    largeScreen && getCreatedBy(),
    getDateUpdated(),
    largeScreen && getUpdatedBy(),
    getRangeColumn(theme, hasFullHistory, rangeAnalysis, primaryRangeAnalysis),
    getStartColumn(),
    getEndColumn(),
    getFrequencyColumn(),
    getReturnForecastColumn(canEditForecasts, onRowUpdate),
    getResidualForecastColumn(canEditForecasts, onRowUpdate),
    getProxyInterpolationColumn(canEditProxies, onRowUpdate),
  ]);
};

/** Simple cell with tooltip indicating content is empty due to no returns */
const getNoReturnsTooltippedCell = (data: BulkManageRow, cellContent: React.ReactNode) => {
  return (
    <DefaultCell>
      <StyledTooltip usePortal maxWidth={240} content={<div>This item does not have a return stream.</div>}>
        {cellContent}
      </StyledTooltip>
    </DefaultCell>
  );
};

/* Convenience method to sort any row by a property while ensuring that excluded or secondary rows are always last. */
export const bulkManageSortFunction =
  (getter: (r: BulkManageRow) => number | string | undefined, exclude?: (r: BulkManageRow) => boolean) =>
  (valueA, valueB, nodeA: IRowNode<BulkManageRow>, nodeB: IRowNode<BulkManageRow>, isDescending: boolean) => {
    const isSecondary = (r: BulkManageRow) => r.secondary ?? false;

    const rowA = isDescending ? nodeB.data : nodeA.data;
    const rowB = isDescending ? nodeA.data : nodeB.data;

    if (!rowA || !rowB) return 0;

    return (
      compareExclude(rowA, rowB, exclude) ??
      compareExclude(rowA, rowB, isSecondary) ??
      compare(getter(rowA), getter(rowB), isDescending)
    );
  };

/* Ensures that rows marked as excluded are always after non-excluded rows. */
const compareExclude = (a: BulkManageRow, b: BulkManageRow, exclude?: (r: BulkManageRow) => boolean) => {
  if (exclude?.(a) && exclude?.(b)) {
    return 0;
  }
  if (exclude?.(a)) {
    return 1;
  }
  if (exclude?.(b)) {
    return -1;
  }
  return undefined;
};

const compare = <T extends (number | undefined) | (string | undefined)>(a: T, b: T, isDescending: boolean) => {
  if (a === b) {
    return 0;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b) * (isDescending ? -1 : 1);
  }

  return ((a ?? 0) > (b ?? 0) ? 1 : -1) * (isDescending ? -1 : 1);
};

const DefaultCell = styled.div<{ disabled?: boolean }>`
  ${({ disabled }) =>
    disabled &&
    css`
      color: ${GetColor.HintGrey};
    `}
`;

const StyledTooltip = styled(Tooltip)`
  width: 100%;
  height: 100%;
  display: block;
`;

/* Internal cell padding to trigger tooltips to show on no text */
const TooltipTrigger = styled.div`
  height: 16px;
  width: 100%;
`;

const RangeCell = styled.div`
  width: 100%;
  height: 100%;
  padding: 0;
`;

const RangeBar = styled.div<{
  widthPercentage: number;
  leftMarginPercentage: number;
  dashed?: boolean;
  color: string;
}>`
  ${({ dashed, widthPercentage, leftMarginPercentage, color }) => `
    background: ${
      dashed ? `repeating-linear-gradient(to right, ${color} 0, ${color} 8px, transparent 8px,transparent 16px)` : color
    };
    height: 8px;
    width: ${widthPercentage * 100}%;
    margin-left: ${leftMarginPercentage * 100 ?? 0}%;
    padding: 0;
    top: calc(50% - 4px);
    position: absolute;
    `}
`;

const OverlapBar = styled.div<{
  widthPercentage: number;
  leftMarginPercentage: number;
}>`
  background-color: ${GetColor.PaleTeal};
  height: 100%;
  width: ${({ widthPercentage }) => widthPercentage * 100}%;
  margin-left: ${({ leftMarginPercentage }) => leftMarginPercentage * 100 ?? 0}%;
  padding: 0;
  top: 0;
  opacity: 0.5;
  border-left: 1px solid ${GetColor.LightGrey};
  border-right: 1px solid ${GetColor.LightGrey};
  position: absolute;
`;

const StyledBodyHighlight = styled(BodyHighlight)`
  font-weight: bold;
`;

const ForecastInvestmentCell = styled(DefaultCell)`
  display: flex;
  justify-content: flex-end;

  > span {
    margin-left: auto;
  }
`;

const StyledResidualSpan = styled.span<{ useRightMargin?: boolean }>`
  ${({ useRightMargin }) =>
    useRightMargin &&
    css`
      margin-right: 5px;
    `}
`;
